import { useEffect, useState } from 'react';
import { UncategorizedIcon } from '@/shared/config';
import { kpiApi, apiErrorText, type KpiSettings } from '@/shared/api';
import { useAuthStore } from '@/features/auth';
import { Button, IntegerInput, toast } from '@/shared/ui';
import { cn } from '@/shared/lib/cn';
import styles from './SatisfactionLevelsCard.module.css';

export const DefaultPointsCard: React.FC = () => {
  const canEdit = useAuthStore((s) => s.hasPermission('manage_kpi'));
  const [settings, setSettings] = useState<KpiSettings | null>(null);
  const [points, setPoints] = useState('');
  const [target, setTarget] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    kpiApi
      .settings()
      .then((res) => {
        const value = res.data;
        setSettings(value);
        setPoints(String(value.defaultBasePoints ?? 0));
        setTarget(value.defaultTargetMinutes == null ? '' : String(value.defaultTargetMinutes));
      })
      .catch((err) => {
        toast.error(apiErrorText(err, 'Не удалось загрузить настройки KPI'));
        setSettings(null);
      });
  }, []);

  const save = async () => {
    const basePoints = Number(points.replace(',', '.'));
    if (!Number.isFinite(basePoints) || basePoints < 0) {
      toast.error('Очки — целое число от нуля');
      return;
    }
    const targetMinutes = target.trim() === '' ? null : Number(target.replace(',', '.'));
    if (targetMinutes != null && (!Number.isFinite(targetMinutes) || targetMinutes < 0)) {
      toast.error('Целевое время — целое число минут или пусто');
      return;
    }

    setSaving(true);
    try {
      const res = await kpiApi.updateSettings({
        defaultBasePoints: Math.floor(basePoints),
        defaultTargetMinutes: targetMinutes == null ? null : Math.floor(targetMinutes),
      });
      setSettings(res.data);
      toast.success('Настройки сохранены');
    } catch (err) {
      toast.error(apiErrorText(err, 'Не удалось сохранить настройки'));
    } finally {
      setSaving(false);
    }
  };

  if (!settings) return null;

  return (
    <div className={styles.card}>
      <h3 className={styles.heading}>
        <UncategorizedIcon size={16} aria-hidden /> Заявки без категории
      </h3>
      <p className={styles.note}>
        Заявку можно подать в свободной форме — категории у неё нет. Эти величины применяются
        только к таким заявкам: у остальных работают настройки их категории, зафиксированные при
        подаче. Ноль — не начислять.
      </p>
      <div className={styles.rows}>
        <div className={cn(styles.row, styles.rowPair)}>
          <span className={styles.rating}>Очки</span>
          <IntegerInput
            aria-label="Базовые очки за заявку без категории"
            value={points}
            disabled={!canEdit || saving}
            onChange={setPoints}
          />
        </div>
        <div className={cn(styles.row, styles.rowPair)}>
          <span className={styles.rating}>Цель, мин</span>
          <IntegerInput
            aria-label="Целевое время закрытия для заявки без категории"
            placeholder="пусто — по сроку SLA"
            value={target}
            disabled={!canEdit || saving}
            onChange={setTarget}
          />
        </div>
      </div>
      {canEdit && (
        <div className={styles.actions}>
          <Button variant="primary" size="sm" onClick={save} disabled={saving}>
            Сохранить
          </Button>
        </div>
      )}
    </div>
  );
};
